import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { DefaultTable } from 'Components/common/DefaultTable';
import { Main as Container } from 'Components/Main';
import { SectionBanner } from 'Components/common';
import { tableHeader } from './table-header.js';
import { PageLoaderList } from 'Components/PageLoader/List';
import { deleteCompany, getCompaniesPaged } from 'services/company-service';
import { useAuthUser } from 'store/auth';
import { useSelector } from 'react-redux';
import { hasModulePermission } from 'utils/permission_utils';
import { Button, Card, Dialog, PageHeader, TextField, PlusSvg } from 'Components/ui';
import { NoData } from 'Components/NoData';
import { Pagination, Stack } from '@mui/material';
import { Column, Row } from 'Components/Grid';
import { useForm } from 'react-hook-form';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';

export const Companies = () => {
  const { t } = useTranslation(['company']);
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const { register, watch, reset } = useForm({
    defaultValues: {
      name: '',
    },
  });
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const {
    isLoading,
    error,
    data: pagedCompanies,
  } = useQuery(['getCompanies', reFetch, page], async () => {
    const result = await getCompaniesPaged(page, 15);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const hideDelete = () => {
    return !hasModulePermission(user.permissions, ['super_admin_global', 'admin_global']);
  };

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteCompany(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!reFetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar removar a Empresa!');
      }
    } catch (error) {
      Dialog.alert({
        title: t('company:companies.delete.errorTitle'),
        message: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const getFilteredCompanies = () => {
    if (!pagedCompanies?.data) return [];
    const term = (appliedNameFilter || '').trim().toLowerCase();
    if (term === '') return pagedCompanies.data;
    return pagedCompanies.data.filter(company => (company.name || '').toLowerCase().includes(term));
  };

  return (
    <Container>
      <PageHeader
        title={t('company:companies.title')}
        segments={[
          { label: t('common:breadcrumbs.generalSettings') },
          { label: t('company:companies.title') },
        ]}
        actions={<Button onClick={() => navigate('add')} startIcon={<PlusSvg />}>{t('company:companies.addButton')}</Button>}
      />
      <SectionBanner
        title={t('company:companies.info.title')}
        description={t('company:companies.info.description')}
      />
      <Card className="mb-2 mt-3" sx={{ padding: '24px' }}>
        <Row className="mb-3" sx={{ alignItems: 'center' }}>
          <Column xs={12} md={6}>
            <TextField
              {...register('name')}
              label={t('company:companies.filter.name')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Column>
          <Column xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%'
            }}>
              <Button
                size="sm"
                variant="secondary"
                sx={{
                  width: '120px',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  height: '36px',
                  fontWeight: 500
                }}
                onClick={() => {
                  reset();
                  setAppliedNameFilter('');
                  setRefetch(!reFetch);
                }}
              >
                {t('company:companies.filter.clear')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                sx={{
                  width: '120px',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  height: '36px',
                  fontWeight: 500
                }}
                endIcon={<SearchOutlinedIcon />}
                onClick={() => {
                  setAppliedNameFilter((watch('name') || '').trim());
                  setRefetch(!reFetch);
                }}
              >
                {t('company:companies.filter.search')}
              </Button>
            </div>
          </Column>
        </Row>
      </Card>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('company:companies.error')}</div>}
      {!isLoading && !error && pagedCompanies.data?.length === 0 && (
        <NoData description={t('company:companies.noData')} />
      )}
      {!isLoading && !error && pagedCompanies.data.length > 0 && (
        <div className="container">
          <DefaultTable
            rows={tableHeader(t)}
            cells={getFilteredCompanies()}
            onClickDelete={onDelete}
            link="companies"
            hideDelete={hideDelete()}
          />
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedCompanies.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          <Dialog
            open={open}
            onConfirm={onConfirm}
            onCancel={onClose}
            message={t('company:companies.delete.confirm', { name: itemToDelete?.name })}
          />
        </div>
      )}
    </Container>
  );
};
